import AI_LIST_RAW from "./gs1_ai.json"
const AI_LIST = AI_LIST_RAW as AISpec[]

// ASCII / special tokens
const GS_CHAR = String.fromCharCode(29) // group separator
const NUL_CHAR = String.fromCharCode(0)

export interface parseGS1StringInterface {
  gtin?: string
  batchExpiry?: string
  batchNo?: string
  serial?: string
  systemGenerated?: string
  secondaryIdWithTS?: string
}

// In-memory AI list (populated by loadGS1AIs)
export type GS1ParsedResult = {
  ai: string
  value: string
  desc?: string
  key?: string
}

type AISpec = {
  ai: string // e.g. "01", "240"
  type: "Fixed" | "Variable"
  length?: number // exact length for Fixed
  minLength?: number // optional
  maxLength?: number // max length for Variable
  desc?: string
  key?: string
}

// Reserved type for potential stronger typing of getActionIdFromGS1DataMatrix return in future
type GetActionIdFromGS1DataMatrixReturn = {
  actionId: string
  actionIdCandidates: string[]
  parsedData: parseGS1StringInterface
  rawData: GS1ParsedResult[]
}

function matchAIAt(s: string, pos: number): AISpec | null {
  for (const spec of AI_LIST) {
    const ai = spec.ai
    if (s.substr(pos, ai.length) === ai) return spec
  }
  return null
}

function parseGS1DataMatrix(inputRaw: string): GS1ParsedResult[] {
  if (inputRaw == null) return []

  if (inputRaw.includes("~")) {
    return [
      {
        ai: "~",
        desc: "system generated",
        key: "system_generated",
        value: inputRaw
      }
    ]
  } else if (inputRaw.length < 25) {
    if (Number.isInteger(Number(inputRaw))) {
      return [
        {
          ai: "01",
          desc: "GTIN",
          key: "gtin",
          value: inputRaw
        }
      ]
    } else if (inputRaw.includes("ts")) {
      return [
        {
          ai: "sid",
          desc: "Product Secondary ID",
          key: "secondaryId",
          value: inputRaw
        }
      ]
    }
  }
  let s = inputRaw.replace(/\[GS\]/gi, GS_CHAR).replace(/\[NUL\]/gi, NUL_CHAR)

  const results: GS1ParsedResult[] = []
  let idx = 0

  while (idx < s.length) {
    if (s[idx] === GS_CHAR || s[idx] === NUL_CHAR) {
      idx++
      continue
    }

    const spec = matchAIAt(s, idx)
    if (!spec) {
      idx++
      continue
    }

    const ai = spec.ai
    const desc = spec.desc
    const key = spec.key
    idx += ai.length
    if (spec.type === "Fixed") {
      const expectedLen = spec.length || spec.maxLength || 0
      const value = s.substr(idx, expectedLen)
      results.push({ ai, desc, key, value })
      idx += value.length
    } else {
      let end = idx
      const maxLen = spec.maxLength || 30

      while (end < s.length && s[end] !== GS_CHAR && s[end] !== NUL_CHAR && end - idx < maxLen) {
        end++
      }

      const value = s.substring(idx, end)
      idx = end

      if (s[idx] === GS_CHAR || s[idx] === NUL_CHAR) idx++

      results.push({ ai, desc, key, value })
    }
  }

  return results
}

function getActionIdFromGS1DataMatrix(barcode: string): GetActionIdFromGS1DataMatrixReturn {
  let parsedData = parseGS1DataMatrix(barcode)
  let result = {} as GetActionIdFromGS1DataMatrixReturn

  result.rawData = parsedData
  result.parsedData = parsedData.reduce<parseGS1StringInterface>((acc, val) => {
    switch (val.ai) {
      case "~":
        acc.systemGenerated = val.value
        break
      case "01":
        acc.gtin = val.value
        break
      case "10":
        acc.batchNo = val.value
        break
      case "21":
        acc.serial = val.value
        break
      case "17":
        acc.batchExpiry = val.value
        break
      case "sid":
        acc.secondaryIdWithTS = val.value
        break
    }
    return acc
  }, {})

  if (result?.parsedData?.systemGenerated) {
    result.actionId = result?.parsedData?.systemGenerated
    return result
  } else if (result?.parsedData?.secondaryIdWithTS) {
    result.actionId = result?.parsedData?.secondaryIdWithTS
    return result
  }

  // populated action id
  const actionIdCandidates: string[] = []

  if (result?.parsedData.gtin) {
    result.actionId = result?.parsedData.gtin
    actionIdCandidates.unshift(result.actionId)
  }
  if (result?.parsedData.batchNo) {
    result.actionId += `|${result?.parsedData.batchNo}`
    actionIdCandidates.unshift(result.actionId as string)
  }
  if (result?.parsedData.serial) {
    result.actionId += `|${result?.parsedData.serial}`
    actionIdCandidates.unshift(result.actionId as string)
  }
  result.actionIdCandidates = actionIdCandidates

  return result
}

export const GS1Parser = {
  parse: parseGS1DataMatrix,
  parseAndExtractActionId: getActionIdFromGS1DataMatrix
}
